/**
 * The Confirm All gate (Spec #34102 AC2 / Business Rule 4, extended by AB#35149).
 *
 * Four conditions, all required:
 *
 *  1. no outstanding rows - nothing NeedsEntry or unacknowledged-LowConfidence;
 *  2. patient and requestor confirmed (Business Rule 3, real F1c match state);
 *  3. no Request Type/Purpose sync in flight;
 *  4. Quick Entry is not awaiting intake.
 *
 * Condition 4 (AB#39809) covers a gap the other three cannot see. While the rows are hidden behind
 * the awaiting-intake placeholder there are no rows to be outstanding, so condition 1 reads zero
 * and the footer offered Confirm All over a form that is not on screen. It is deliberately keyed on
 * isQuickEntryAwaitingIntake() rather than on "is there an intake record": AB#38014's confirmed
 * stuck state makes that false, so it keeps its enabled button and its editable rows.
 *
 * Condition 3 is the new one. Confirming a Type/Purpose change fires a backend round-trip that can
 * reset another row - Records Requested, when the new purpose offers a different record-type set.
 * The round-trip is asynchronous, so until it lands the screen still shows the OLD, confirmed rows
 * while the outstanding count reads zero. A specialist moving quickly could commit the whole
 * request in that window, confirming values that were a moment from being replaced.
 *
 * The other half of the protection is not a rule here: when the sync lands and a row was actually
 * reset, that row goes back into the editing set, which AB#35073 downgrades to NeedsEntry. It then
 * counts as outstanding through condition 1, so the specialist has to look at the reloaded row and
 * confirm it before Confirm All returns. Reusing the outstanding count keeps the header's
 * "N need your attention" tally and this gate from ever disagreeing.
 */
export function isConfirmAllEnabled(
    outstandingCount: number,
    patientRequestorConfirmed: boolean,
    purposeSyncInFlight: boolean,
    awaitingIntake: boolean,
): boolean {
    return outstandingCount === 0 && patientRequestorConfirmed && !purposeSyncInFlight && !awaitingIntake;
}

export default isConfirmAllEnabled;
